import { useState, useEffect, useCallback } from "react";
import { supabase, isSupabaseEnabled } from "../lib/supabase";
import type { SubscriptionTier } from "../components/billing/BillingPage";

interface UseSubscriptionResult {
  tier: SubscriptionTier;
  isInvestor: boolean;
  isFree: boolean;
  isInTrial: boolean;
  trialExpired: boolean;
  trialEndsAt: Date | null;
  trialDaysLeft: number;
  hasFullAccess: boolean;
  isReadOnly: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Returns the current user's subscription tier and trial state.
 *
 * Trial model:
 * - New users get 14 days full access (trial_ends_at set on signup)
 * - After 14 days without a paid subscription → read-only mode
 * - Paying investors → full access forever
 * - hasFullAccess = isInvestor OR isInTrial
 * - isReadOnly = trialExpired (free tier + no active trial)
 */
export function useSubscription(userId?: string): UseSubscriptionResult {
  const [tier, setTier] = useState<SubscriptionTier>("free");
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchTier = useCallback(async () => {
    if (!userId || !isSupabaseEnabled() || !supabase) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from("user_profiles")
        .select("subscription_tier, trial_ends_at")
        .eq("id", userId)
        .maybeSingle();
      if (data?.subscription_tier) {
        setTier(data.subscription_tier as SubscriptionTier);
      }
      setTrialEndsAt(data?.trial_ends_at ? new Date(data.trial_ends_at) : null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchTier();
  }, [fetchTier]);

  const now = new Date();
  const isInvestor = tier === "investor";
  const isFree = tier === "free";
  const isInTrial = isFree && trialEndsAt !== null && trialEndsAt > now;
  const trialExpired = isFree && (trialEndsAt === null || trialEndsAt <= now);
  const trialDaysLeft = isInTrial
    ? Math.ceil((trialEndsAt!.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const hasFullAccess = isInvestor || isInTrial;
  const isReadOnly = trialExpired;

  return {
    tier,
    isInvestor,
    isFree,
    isInTrial,
    trialExpired,
    trialEndsAt,
    trialDaysLeft,
    hasFullAccess,
    isReadOnly,
    loading,
    refresh: fetchTier,
  };
}
